﻿package com.mgacreative.hotelru.feature.admin.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotelru.core.tenant.TenantSession
import com.mgacreative.hotelru.domain.model.PricePeriod

import com.mgacreative.hotelru.domain.repository.PricingRepository
import com.mgacreative.hotelru.domain.repository.RoomRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class AdminPricingViewModel(
    private val pricingRepository: PricingRepository,
    private val roomRepository: RoomRepository,
    private val tenantSession: TenantSession
) : ViewModel() {

    private val _pricePeriods = MutableStateFlow<List<PricePeriod>>(emptyList())
    val pricePeriods: StateFlow<List<PricePeriod>> = _pricePeriods.asStateFlow()

    val roomTypes: List<String> = com.mgacreative.hotelru.domain.model.RoomTypes.list

    fun loadData() {
        viewModelScope.launch {
            _pricePeriods.value = pricingRepository.getAllPricePeriods()
        }
    }

    fun addBulkPricePeriods(name: String, start: String, end: String, prices: Map<String, Double>) {
        viewModelScope.launch {
            val tenantId = tenantSession.currentTenantId ?: "00000000-0000-0000-0000-000000000000"
            val newPeriods = prices.map { (roomType, price) ->
                PricePeriod(
                    tenantId = tenantId,
                    roomType = roomType,
                    name = name,
                    startDate = start,
                    endDate = end,
                    price = price
                )
            }
            
            // Sequentially add them for now to ensure repository handles it
            val addedList = mutableListOf<PricePeriod>()
            for (period in newPeriods) {
                val added = pricingRepository.addPricePeriod(period)
                if (added != null) addedList.add(added)
            }
            
            _pricePeriods.value = _pricePeriods.value + addedList
        }
    }

    fun updatePricePeriod(id: String, name: String, start: String, end: String, price: Double) {
        viewModelScope.launch {
            val success = pricingRepository.updatePricePeriod(id, name, start, end, price)
            if (success) {
                _pricePeriods.value = _pricePeriods.value.map {
                    if (it.id == id) it.copy(name = name, startDate = start, endDate = end, price = price) else it
                }
            }
        }
    }

    fun deletePricePeriod(id: String) {
        viewModelScope.launch {
            val success = pricingRepository.deletePricePeriod(id)
            if (success) {
                _pricePeriods.value = _pricePeriods.value.filter { it.id != id }
            }
        }
    }

    fun deletePricePeriods(ids: List<String>) {
        viewModelScope.launch {
            // Delete sequentially for simplicity, since it's an admin operation.
            // Ideally this would be a bulk delete operation in the repository.
            val successfulDeletions = mutableSetOf<String>()
            for (id in ids) {
                if (pricingRepository.deletePricePeriod(id)) {
                    successfulDeletions.add(id)
                }
            }
            if (successfulDeletions.isNotEmpty()) {
                _pricePeriods.value = _pricePeriods.value.filter { it.id !in successfulDeletions }
            }
        }
    }
}

